import { composeHOCs } from 'shared/utils/composeHOCs';
import { withIssueForm } from 'components/common/withIssueForm';
import { withProcessesData } from 'components/common/withProcessesData';
import { withSelectProject } from 'components/common/withSelectProject';
import { Spinner } from 'components/core';
import { useCompaniesStore } from 'store/company';
import { withUsers } from 'components/dataProviders/withUsers';
import React from 'react';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { MemoAnalytics } from 'views/analytics';
import { AnalyticsChartRuntimeProvider } from 'charts/hooks/AnalyticsChartRuntimeProvider';
import { AnalyticsPrivateRoute } from '../PrivateRoute';
import { withInspectionTemplatesStore } from 'store/inspection';

/**
 * One component for good: a component made on each render (an inline arrow)
 * remounted the whole module whenever the route rendered again (a reload of
 * the companies on the return to the tab) and every chart played its
 * animation again (PT-6804).
 */
function AnalyticsPage(): React.ReactElement {
  const id = useAppUrlParams(UrlParamName.CHART_ID);
  return <MemoAnalytics id={id} />;
}

const AnalyticsRoute = (): React.ReactElement => {
  // The flag only: a reload of the companies (new items, same flag) does not render the route again.
  const loadingCompanies = useCompaniesStore((state) => state.data.loading);

  if (loadingCompanies) {
    return <Spinner reason='AnalyticsRoute loading' />;
  }

  return (
    <AnalyticsChartRuntimeProvider>
      <AnalyticsPrivateRoute element={AnalyticsPage} />
    </AnalyticsChartRuntimeProvider>
  );
};

export const MemoAnalyticsRoute = React.memo(
  composeHOCs(
    withSelectProject,
    withProcessesData,
    withUsers,
    withIssueForm,
    withInspectionTemplatesStore
  )(AnalyticsRoute)
);
